import { Component, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { ModalService } from '../../../services/modal/modal.service';
import { AchooPopupComponent } from '../../../core/achoo-popup/achoo-popup.component';
import { UserService } from '../../../services/user.service';
import { InfluencerGroup, BulkUploadInfluencer } from '../../../../models/influencer-group';
import { InvalidAction, BlockedReason, WallType } from '../../../../models/user';
import { EventTrackerService, UserActionType, Page, TargetType, ActionSource, ActionDetails } from '../../../services/event-tracker/event-tracker.service';
import { BulkUploadInfluencerModalComponent } from './bulk-upload-influencer-modal/bulk-upload-influencer-modal.component';
import { CommonConstant } from '../../../app.constants';
import { TranslateService } from '@ngx-translate/core';
import { ObjectUtils } from '../../../utils/object.utils';
import { InfluencerGroupsService } from '../../influencer-groups/influencer-groups.service';

@Component({
  selector: 'bulk-upload-influencer',
  templateUrl: './bulk-upload-influencer.component.html',
  styleUrls: ['./bulk-upload-influencer.component.scss']
})
export class BulkUploadInfluencerComponent {

  @ViewChild('BulkUploadInfluencerPopup') bulkUploadInfluencerPopup: AchooPopupComponent;
  @ViewChild('BulkUploadInfluencerModalComponent') bulkUploadInfluencerModalComponent: BulkUploadInfluencerModalComponent;
  @ViewChild('bulkUploadNotificationPopup') bulkUploadNotificationPopup: AchooPopupComponent;

  @Input() influencerGroup: InfluencerGroup;
  @Input() hasSelectedInfluencers = false;


  @Output() closeBottomBarCallback: EventEmitter<any> = new EventEmitter<any>();
  @Output() afterUploadBulkInfluencerEvent: EventEmitter<any> = new EventEmitter<any>();

  public temporaryHidden = false;
  public modelTitle: string;
  public popupMessage: string;
  public bulkUploadInfluencer: BulkUploadInfluencer = new BulkUploadInfluencer();
  protected bulkUploadInfluencerPopupRef;

  constructor(private modalService: ModalService,
              private userService: UserService,
              private translate: TranslateService,
              private influencerGroupsService: InfluencerGroupsService,
              private eventTracker: EventTrackerService) { }

  showBulkUploadInfluencerModal() {
    if (this.userService.isFreeUser()) {
      this.userService.doInvalidActionByLevel('Add to Group');
      this.trackUserInvalidAction();
    } else {
      this.closeBottomBarCallback.emit({
        isTemporaryHidden: true
      });
      this.bulkUploadInfluencerPopupRef = this.modalService.openInCenter(this.bulkUploadInfluencerPopup, null, 'campaign-confirm-popup');
    }
  }

  beforeUploadBulkInfluencer() {
    this.modalService.closeSinglePopup(this.bulkUploadInfluencerPopupRef);
    this.showPopup(this.translate.instant('bulk_upload_influencer.modal.uploading_notification_title'), this.translate.instant('bulk_upload_influencer.modal.uploading_notification_content'));
    this.callService();
  }

  closeBulkUploadInfluencerModal(evt?) {
    // clean cached bulk influencer list
    this.bulkUploadInfluencer.influencers = [];
    this.bulkUploadInfluencer.shouldNotify = false;
    this.bulkUploadInfluencerModalComponent.bulkUploadInfluencerInputValue = null;
    this.closeBottomBarCallback.emit({
      bulkUploadInfluencers: evt,
      isTemporaryHidden: false
    });
    if (this.bulkUploadInfluencerPopupRef) {
      this.modalService.closeSinglePopup(this.bulkUploadInfluencerPopupRef);
    }
  }

  private showPopup(modelTitle: string, msgContent: string) {
    this.modelTitle = modelTitle;
    this.popupMessage = msgContent;
    this.modalService.open(this.bulkUploadNotificationPopup, null, true);
  }

  private callService() {
    const bulkUploadInfluencerData = ObjectUtils.deepClone(this.bulkUploadInfluencer);
    this.influencerGroupsService.uploadBulkInfluencer(this.influencerGroup.influencer_group_id, this.bulkUploadInfluencer).subscribe(
      (res) => {
        this.sendMixpanelEvents(null);
        const message = this.bulkUploadInfluencer.shouldNotify ? this.translate.instant('bulk_upload_influencer.modal.success_with_notify') : this.translate.instant('bulk_upload_influencer.modal.success_without_notify');
        this.showPopup(this.translate.instant('notification.success'), message);
        setTimeout(() => {
          this.closeBulkUploadInfluencerModal(bulkUploadInfluencerData.influencers);
        }, CommonConstant.TRANSITION_TIME);

      }, (error) => {
        this.bulkUploadInfluencerPopupRef = this.modalService.openInCenter(this.bulkUploadInfluencerPopup, null, 'campaign-confirm-popup');
        if (error && error.error && error.error.message) {
          this.handleError(error.error);
        } else {
          this.showPopup(this.translate.instant('notification.error'), this.translate.instant('bulk_upload_influencer.modal.api_failed'));
        }
      }
    );
  }

  private handleError(error) {
    const errorMsg = this.translate.instant('bulk_upload_influencer.modal.api_general_error');
    const maxValue = this.userService.maxGroupInfluencers;
    this.showPopup(this.translate.instant('notification.error'), errorMsg);
    this.sendMixpanelEvents(this.influencerGroupsService.extractLimitInfoFromErrorMessage(errorMsg, this.bulkUploadInfluencer.influencers.length, maxValue, false));
  }

  private sendMixpanelEvents(limitInfo) {
    if (!limitInfo) {
      this.trackUserAction();
    } else {
      this.trackUserInvalidAction(limitInfo);
    }
  }

  private trackUserAction() {
    this.eventTracker.trackUserAction(UserActionType.ACTION, Page.INFLUENCER_GROUP, {
      id: this.influencerGroup.influencer_group_id,
      name: this.influencerGroup.influencer_group_name,
      type: TargetType.INFLUENCER_GROUP
    }, {
      action: UserActionType.UPDATE,
      source: ActionSource.ACTION_VIEW,
      actionDetails: ActionDetails.BULK_UPLOAD,
      ...this.influencerGroup
    }, undefined, null, null);
  }

  private trackUserInvalidAction(limitInfo?) {
    let detailAction;
    if (limitInfo) {
      detailAction = new InvalidAction(true, limitInfo.blockedReason, limitInfo.wallType, limitInfo.wallName, true,
        limitInfo.maxValue, limitInfo.desiredValue, this.userService.accountLevelName);
    } else {
      detailAction = new InvalidAction(true, BlockedReason.ACCOUNT_WALL, WallType.FEATURE_UNAVAILABLE,
        'Advanced Groups', false, null, null, this.userService.accountLevelName);
    }
    this.eventTracker.trackUserInvalidAction(detailAction, UserActionType.ACTION, Page.INFLUENCER_GROUP, {
      id: this.influencerGroup.influencer_group_id,
      name: this.influencerGroup.influencer_group_name,
      type: TargetType.INFLUENCER_GROUP
    }, {
      action: UserActionType.UPDATE,
      source: ActionSource.ACTION_VIEW,
      actionDetails: ActionDetails.BULK_UPLOAD,
      ...this.influencerGroup
    }, undefined, null, null);
  }

}



// WEBPACK FOOTER //
// ./src/app/main/bottombar-influencer-fragement/bulk-upload-influencer/bulk-upload-influencer.component.ts